/**
 * Debug-only: preview today's master model or browse / validate the full library.
 */
import React, { useMemo, useState } from 'react';
import {
  DAILY_POOL,
  MODEL_CATALOG,
  YEAR_THEMES,
  getDailyModelId,
  getModelById,
  getYearDayEntry,
  listModelIds,
} from '../game/voxel/models';
import { CATEGORY_META } from '../game/voxel/modelTaxonomy';
import { buildValidatedSlices, publicShellPayload } from '../game/voxel/slicePipeline';
import HologramViewport, { FRONT_YAW } from './HologramViewport';

function ModelPreview({ model, height = 260 }) {
  const shell = useMemo(
    () => (model ? publicShellPayload(model, null) : null),
    [model?.id]
  );
  if (!shell) {
    return (
      <div style={{ padding: 24, color: '#f88', border: '1px solid #633' }}>
        No shell payload for this model.
      </div>
    );
  }
  return (
    <div style={{ minHeight: height }}>
      <div style={{ fontSize: '0.7rem', opacity: 0.6, marginBottom: 6 }}>
        {shell.voxels?.length || 0} surface voxels
      </div>
      <HologramViewport
        key={model.id}
        size={shell.size}
        voxels={shell.voxels}
        palette={shell.palette}
        grayscale={false}
        colorBlend={1}
        autoRotate
        presentation="trophy"
        initialYaw={FRONT_YAW}
        height={height}
      />
    </div>
  );
}

function gateLabel(entry, live) {
  if (live?.ok) return { text: 'PASS', color: '#6faf7a' };
  if (entry?.ok) return { text: 'PASS', color: '#6faf7a' };
  const reasons = live?.reasons || entry?.reasons || [];
  if (!reasons.length) return { text: '—', color: '#a89b91' };
  return { text: `FAIL · ${reasons.join(', ')}`, color: '#e8772a' };
}

/**
 * @param {'master' | 'library'} mode
 * @param {() => void} onClose
 */
const DebugModelBrowser = ({ mode = 'master', onClose }) => {
  const date = useMemo(() => new Date().toISOString().split('T')[0], []);
  const dailyId = useMemo(() => getDailyModelId(date), [date]);
  const yearDay = useMemo(() => getYearDayEntry(date), [date]);

  const catalog = useMemo(() => {
    if (MODEL_CATALOG.length) return MODEL_CATALOG;
    return listModelIds().map((id) => {
      const m = getModelById(id);
      return m
        ? {
            id,
            name: m.name,
            category: m.category,
            size: m.size,
            source: m.source,
            license: m.license,
            ok: DAILY_POOL.includes(id),
            reasons: [],
          }
        : null;
    }).filter(Boolean);
  }, []);

  const categories = useMemo(() => {
    const set = new Set(catalog.map((c) => c.category).filter(Boolean));
    const ordered = CATEGORY_META.map((m) => m.id).filter((id) => set.has(id));
    for (const id of set) if (!ordered.includes(id)) ordered.push(id);
    return ['ALL', ...ordered];
  }, [catalog]);

  const categoryLabel = (id) => CATEGORY_META.find((m) => m.id === id)?.label || id;

  const [filter, setFilter] = useState('ALL');
  const [query, setQuery] = useState('');
  const [onlyPass, setOnlyPass] = useState(false);
  const [selectedId, setSelectedId] = useState(
    mode === 'master' ? dailyId : catalog[0]?.id || dailyId
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return catalog.filter((c) => {
      if (filter !== 'ALL' && c.category !== filter) return false;
      if (onlyPass && !c.yearEligible) return false;
      if (!q) return true;
      return (
        c.id.toLowerCase().includes(q) ||
        String(c.name || '').toLowerCase().includes(q) ||
        String(c.category || '').toLowerCase().includes(q)
      );
    });
  }, [catalog, filter, query, onlyPass]);

  const selected = getModelById(selectedId);
  const entry = catalog.find((c) => c.id === selectedId) || null;

  const liveGate = useMemo(() => {
    if (!selected || selected.size !== 16) return null;
    try {
      const slices = buildValidatedSlices(selected, 16);
      const voxels = selected.volume.flat(2).filter(Boolean).length;
      const colors = Object.keys(selected.palette || {}).length;
      const uniqueSlices = slices.filter((s) => !s.softValidated).length;
      const reasons = [];
      if (voxels < 80) reasons.push('too_sparse');
      if (voxels > 2800) reasons.push('too_dense');
      if (colors < 2) reasons.push('monochrome');
      if (slices.length < 8) reasons.push('few_slices');
      if (uniqueSlices < 4) reasons.push('few_unique');
      return {
        ok: reasons.length === 0,
        reasons,
        voxels,
        colors,
        slices: slices.length,
        uniqueSlices,
        softSlices: slices.length - uniqueSlices,
      };
    } catch (e) {
      return { ok: false, reasons: ['slice_error'], error: String(e.message || e) };
    }
  }, [selected?.id]);

  const badge = gateLabel(entry, liveGate);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.94)',
        zIndex: 12000,
        padding: 16,
        overflow: 'auto',
        color: '#f3e6d4',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <div>
          <div style={{ fontSize: '0.7rem', opacity: 0.65, letterSpacing: '0.08em' }}>
            DEBUG · {mode === 'master' ? 'MASTER MODEL' : 'MODEL VALIDATOR'}
          </div>
          <h2 style={{ margin: '4px 0 0', color: '#9dffb0' }}>
            {selected?.name || '—'}
          </h2>
          <div style={{ fontSize: '0.75rem', opacity: 0.75, fontFamily: 'JetBrains Mono, monospace' }}>
            {selected?.id} · {selected?.category} · {selected?.size}³
            {selectedId === dailyId ? " · today's daily" : ''}
          </div>
          {yearDay && (
            <div style={{ fontSize: '0.7rem', color: '#ffb68d', marginTop: 4 }}>
              Year theme: {yearDay.theme} ({yearDay.themeId})
            </div>
          )}
        </div>
        <button className="outline-btn" type="button" onClick={onClose}>
          Close
        </button>
      </div>

      {mode === 'library' && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                className="outline-btn"
                onClick={() => setFilter(cat)}
                style={{
                  padding: '4px 10px',
                  fontSize: '0.7rem',
                  borderColor: filter === cat ? '#e8772a' : undefined,
                  background: filter === cat ? 'rgba(232,119,42,0.15)' : undefined,
                }}
              >
                {cat === 'ALL'
                  ? `ALL (${catalog.length})`
                  : `${categoryLabel(cat)} (${catalog.filter((c) => c.category === cat).length})`}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name / id / category"
              style={{
                flex: '1 1 180px',
                minWidth: 140,
                background: '#1f1b19',
                border: '1px solid rgba(247,240,232,0.15)',
                borderRadius: 8,
                color: '#f3e6d4',
                padding: '8px 10px',
              }}
            />
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.75rem' }}>
              <input type="checkbox" checked={onlyPass} onChange={(e) => setOnlyPass(e.target.checked)} />
              Year-eligible only
            </label>
            <span style={{ fontSize: '0.7rem', opacity: 0.6, alignSelf: 'center' }}>
              Showing {visible.length} · year pool {DAILY_POOL.length} · themes {YEAR_THEMES.length}
            </span>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: 8,
              marginBottom: 16,
              maxHeight: 220,
              overflow: 'auto',
              paddingRight: 4,
            }}
          >
            {visible.map((it) => (
              <button
                key={it.id}
                type="button"
                className="outline-btn"
                onClick={() => setSelectedId(it.id)}
                style={{
                  padding: 10,
                  textAlign: 'left',
                  borderColor: selectedId === it.id ? '#3cf' : it.ok ? undefined : 'rgba(232,119,42,0.35)',
                  background: selectedId === it.id ? 'rgba(60,200,255,0.12)' : undefined,
                }}
              >
                <div style={{ fontWeight: 700, fontSize: '0.82rem' }}>{it.name}</div>
                <div style={{ fontSize: '0.62rem', opacity: 0.7 }}>{it.category}</div>
                <div style={{ fontSize: '0.58rem', marginTop: 4, color: it.yearEligible ? '#6faf7a' : '#e8772a' }}>
                  {it.yearEligible
                    ? 'YEAR'
                    : it.ok
                      ? it.dedupeReason || 'held'
                      : `FAIL · ${(it.reasons || []).slice(0, 2).join(',')}`}
                </div>
                {it.id === dailyId && (
                  <div style={{ fontSize: '0.6rem', color: '#7dcea0', marginTop: 4 }}>TODAY</div>
                )}
              </button>
            ))}
          </div>
        </>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.1fr) minmax(220px, 0.9fr)',
          gap: 16,
          alignItems: 'start',
        }}
      >
        <div>
          {selected ? (
            <ModelPreview model={selected} height={mode === 'library' ? 300 : 340} />
          ) : (
            <p style={{ color: '#f88' }}>Model not found in library.</p>
          )}
        </div>
        <div
          style={{
            background: '#1f1b19',
            border: '1px solid rgba(247,240,232,0.1)',
            borderRadius: 12,
            padding: 12,
            fontSize: '0.78rem',
            lineHeight: 1.45,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 8, color: badge.color }}>Gate · {badge.text}</div>
          <div style={{ marginBottom: 6, color: entry?.yearEligible ? '#6faf7a' : '#ffb68d' }}>
            Year pool · {entry?.yearEligible ? 'ELIGIBLE' : entry?.dedupeReason || 'excluded'}
            {entry?.dedupeOf ? ` → kept ${entry.dedupeOf}` : ''}
          </div>
          <div><strong>Name</strong> · {selected?.name || '—'}</div>
          <div><strong>Id</strong> · {selected?.id || '—'}</div>
          <div><strong>Category</strong> · {selected?.category || '—'}</div>
          <div><strong>Size</strong> · {selected?.size}³</div>
          <div><strong>License</strong> · {selected?.license || entry?.license || '—'}</div>
          <div style={{ marginTop: 8 }}>
            <strong>Voxels</strong> · {liveGate?.voxels ?? entry?.voxels ?? '—'} ·{' '}
            <strong>Colors</strong> · {liveGate?.colors ?? entry?.colors ?? '—'}
          </div>
          <div>
            <strong>Slices</strong> · {liveGate?.slices ?? entry?.slices ?? '—'} (unique{' '}
            {liveGate?.uniqueSlices ?? entry?.uniqueSlices ?? '—'} / soft{' '}
            {liveGate?.softSlices ?? entry?.softSlices ?? '—'})
          </div>
          {selected?.answers?.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <strong>Guess answers</strong>
              <div style={{ opacity: 0.8 }}>{selected.answers.join(' · ')}</div>
            </div>
          )}
          {selected?.source && (
            <p style={{ marginTop: 10, fontSize: '0.68rem', opacity: 0.55 }}>{selected.source}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DebugModelBrowser;
